import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {uniqueUuid} from "@vesta/unique/unique";

/**
 * Defines the flow required to get the source credentials.
 */
export enum DAG_DataSourceConnectionStrategy {
    amazonAds = "3a53b93b-e409-4856-aa6b-4625a4442ecc",
    amazonMarketplaceSeller = "0bdbf703-a31a-46d1-9e41-d92e4b705cc1",
    amazonMarketplaceVendor = "92438757-0426-4fad-944f-e16ef504ab5b",
    facebookAds = "f49edd33-9426-4dba-8b62-abf2093b0a0f",
    facebookPageOverviewAndPosts = "6b82a7a6-f1fc-42b7-97b0-af456c74fe98",
    // facebookPageVideoAndReels = "17ad89f2-6ec6-468b-9674-e31fc0a688f7",
    flipkartAds = "25939010-8a2b-4f72-928b-55779b8a04e8",
    googleAds = "800c28ce-43ea-44b8-b6fc-077f44566296",
    googleAnalytics = "c4e16b21-7052-4709-824f-c3c9cb91695b",
    googleMyBusiness = "d5e9dc1f-0b0b-467d-8bbd-7b7ee25b0709",
    googleSearchConsole = "f453d2d9-8cd2-4c76-b9e4-6bff42f043f1",
    instagram = "37d758ae-f590-4d41-9578-f9ed5dbec4c6",
    linkedinAds = "6b9a7eeb-4bac-456b-ab31-d5b48bc5660c",
    linkedinPage = "c497342c-abe5-45a9-826d-933f6271010b",
    shopify = "b84a3317-7296-425a-9367-02b7dd7c3116",
    unicommerce = "240fedb1-3266-40dd-b944-3bbea897a0f7",
    userSupplied = "71c14b30-05e7-4edd-a667-9bef5a0ddedf",
    custom = "0a604ca5-4d62-483c-9fcb-af705b5d5d1e",
    youtube = "3bcd4c2a-45d8-49c3-b226-25433a6e783e",
    uiTest = "1fc7f077-5175-4586-9f8a-66452065987e",
}

export enum DAG_ContinueButtonType {
    default = "d8b77587-a6f5-46f6-bf95-6cc14d13d554",
    googleAuth = "4979dde1-0326-47d8-a6f2-c977e2af6682",
    linkedinAuth = "b414d965-8fd1-429f-9637-f3db00ce0082",

}

export enum DAG_DataSourceConnectionStepType {
    connectDataClusters = "2cd357eb-8e5c-4315-9a75-6b807b5f6c5f",
    connectDataSource = "b07beac2-7cf7-47ce-a0b0-836f9f176460",
    consent = "18a6d751-8d7a-4e4d-badd-569eb75d0bfe",
    contactIntellsysTeam = "70478830-1764-4dd1-8b6b-5a89053f99d2",
    enterFieldsForUserSupplied = "4b720a2c-abfb-4d8f-9b99-026336264606",
    // 48ddf606-d047-4d7b-9352-07a957f0a27d
    oauthAmazon = "17324154-00da-4614-b241-a0bc2bfea484",
    oauthAmazonMarketplace = "472158cd-f80e-4144-9174-b9b317cab4ed",
    oauthFacebook = "87403961-534d-4364-b3be-e66246cd8abf",
    oauthFlipkart = "91687500-6200-478d-b1a8-155d114d5ed8",
    oauthGoogle = "0519b7bf-7862-422b-8f51-a69b2c148592",
    oauthLinkedin = "1eb8e5ec-1379-472d-b74b-3eaab74bf061",
    oauthUnicommerce = "de281e81-6e0c-4a83-ace9-0c2870c50f24",
    selectAccount = "0d3e52e9-7afc-4409-b137-348104e6ef53",
    /**
     * Invalid value, so that the last ternary operator in an exhaustiveness check
     * does not throw an eslint error (plus handles invalid input cases)
     */
    invalidValue = "70ccb7b7-d391-4507-805a-e9fe8a625372",
}

export const oauthConnnectionAttemptStepTypes: Array<DAG_DataSourceConnectionStepType> = [
    DAG_DataSourceConnectionStepType.oauthAmazon,
    DAG_DataSourceConnectionStepType.oauthAmazonMarketplace,
    DAG_DataSourceConnectionStepType.oauthFacebook,
    DAG_DataSourceConnectionStepType.oauthFlipkart,
    DAG_DataSourceConnectionStepType.oauthGoogle,
    DAG_DataSourceConnectionStepType.oauthLinkedin,
    DAG_DataSourceConnectionStepType.oauthUnicommerce,
];

export const dataSourceConnectionStepTypeToDetails: Record<
    DAG_DataSourceConnectionStepType,
    {
        /**
         * Indicates whether this step requires human intervention.
         * eg: enterDetails requires intervention, createDataSource does not.
         */
        requiresHumanIntervention: boolean;
    }
> = {
    [DAG_DataSourceConnectionStepType.connectDataClusters]: {
        requiresHumanIntervention: false,
    },
    [DAG_DataSourceConnectionStepType.connectDataSource]: {
        requiresHumanIntervention: false,
    },
    [DAG_DataSourceConnectionStepType.consent]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.contactIntellsysTeam]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.enterFieldsForUserSupplied]: {
        requiresHumanIntervention: true,
    },
    // 48ddf606-d047-4d7b-9352-07a957f0a27d
    [DAG_DataSourceConnectionStepType.oauthAmazon]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthAmazonMarketplace]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthFacebook]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthFlipkart]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthGoogle]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthLinkedin]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.oauthUnicommerce]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.selectAccount]: {
        requiresHumanIntervention: true,
    },
    [DAG_DataSourceConnectionStepType.invalidValue]: {
        requiresHumanIntervention: true,
    },
};

export type DAG_DataSourceConnectionStep = {
    id: Uuid;
    isResumable: boolean;
} & (
        | {
            type: DAG_DataSourceConnectionStepType.connectDataClusters;
        }
        | {
            type: DAG_DataSourceConnectionStepType.connectDataSource;
            connectionDataExtraFields: Record<
                string,
                {
                    // TODO: Make this idiot-proof. This will cause issues when adding new connectionStrategies.
                    stepId: Uuid;
                    stepDataAttributeName: string;
                }
            >;
        }
        | {
            type: DAG_DataSourceConnectionStepType.consent;
            consentText: string;
            continueButtonType: DAG_ContinueButtonType;
        }
        | {
            type: DAG_DataSourceConnectionStepType.contactIntellsysTeam;
        }
        | {
            type: DAG_DataSourceConnectionStepType.enterFieldsForUserSupplied;
        }
        //   48ddf606-d047-4d7b-9352-07a957f0a27d
        | {
            type: DAG_DataSourceConnectionStepType.oauthAmazon;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthAmazonMarketplace;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthFacebook;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthFlipkart;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthGoogle;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthLinkedin;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.oauthUnicommerce;
            scopes: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.selectAccount;
            //   // TODO: Type this properly
            //   getAccountsQueryFn: (args: {
            //     accessToken: string;
            //     extraFields: Record<string, unknown>;
            //     hadesBaseUrl: string;
            //     workspaceId: Uuid;
            //   }) => Promise<
            //       Result<
            //           Array<{
            //               accountId: Uuid;
            //               accountName: string;
            //               disabled: boolean;
            //               connectionData: Record<string, unknown>;
            //           }>
            //       >
            //   >;
            /**
             * Returns pathname (without origin) - prefix hadesBaseUrl as required. Includes leading slash.
             */
            getAccountsQueryFn_pathname: string;
        }
        | {
            type: DAG_DataSourceConnectionStepType.invalidValue;
        }
    );

export type DAG_DataSourceConnectionStepTypeToStepDataType = {
    [DAG_DataSourceConnectionStepType.connectDataClusters]: object;
    [DAG_DataSourceConnectionStepType.connectDataSource]: {
        dataSourceInstanceIds: Uuid;
    };
    [DAG_DataSourceConnectionStepType.consent]: {
        consentGranted: boolean;
    };
    [DAG_DataSourceConnectionStepType.contactIntellsysTeam]: object;
    [DAG_DataSourceConnectionStepType.enterFieldsForUserSupplied]: {
        // This might become `Record<Uuid, unknown>` when we introduce support for other field types
        fieldValues: Record<Uuid, string>;
    };
    // 48ddf606-d047-4d7b-9352-07a957f0a27d
    [DAG_DataSourceConnectionStepType.oauthAmazon]: object;
    [DAG_DataSourceConnectionStepType.oauthAmazonMarketplace]: object;
    [DAG_DataSourceConnectionStepType.oauthFacebook]: object;
    [DAG_DataSourceConnectionStepType.oauthFlipkart]: object;
    [DAG_DataSourceConnectionStepType.oauthGoogle]: {
        refreshToken: string;
    };
    [DAG_DataSourceConnectionStepType.oauthLinkedin]: object;
    [DAG_DataSourceConnectionStepType.oauthUnicommerce]: object;
    [DAG_DataSourceConnectionStepType.selectAccount]: {
        selectedAccounts: Array<DAG_AccountAvailableForConnection>;
    };
    [DAG_DataSourceConnectionStepType.invalidValue]: object;
};

export type DAG_DataSourceConnectionStrategyDetails = {
    connectionSteps: Array<DAG_DataSourceConnectionStep>;
};

const UI_TEST_MOCK_ACCOUNTS_PATHNAME = "/test/ui-test/get-accessible-accounts";
const GET_ACCESSIBLE_ACCOUNTS_PATHNAME = "/ui/data-sources/connection-strategies/get-accessible-accounts";

// TODO: Store this along with the DataSource
export const dataSourceConnectionStrategyDetailsMap: Record<DAG_DataSourceConnectionStrategy, DAG_DataSourceConnectionStrategyDetails> = {
    [DAG_DataSourceConnectionStrategy.amazonAds]: {
        connectionSteps: [
            {
                id: uniqueUuid`0e6e3101-aa21-4a8d-b5ac-d2fec361e46f`,
                type: DAG_DataSourceConnectionStepType.oauthAmazon,
                scopes: "advertising::campaign_management",
                isResumable: true,
            },
            {
                id: uniqueUuid`b042ea4a-3a9b-4924-9a57-1656bd6f0846`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.amazonAds}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`b2816cdb-ffb1-4a68-aa0b-9a1f0bf2d77d`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`0e6e3101-aa21-4a8d-b5ac-d2fec361e46f`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`86d65b46-bd13-4aa7-87e3-7e80740658ef`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.amazonMarketplaceSeller]: {
        connectionSteps: [
            {
                id: uniqueUuid`2ac03182-8e14-4193-9e16-eda57f3affbe`,
                type: DAG_DataSourceConnectionStepType.oauthAmazonMarketplace,
                scopes: "",
                isResumable: true,
            },
            {
                id: uniqueUuid`aca5b349-f9c0-4e35-b4dc-943ef8a1c1ed`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.amazonMarketplaceSeller}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`23434cfc-298c-4c36-b68e-6f308b04e123`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`2ac03182-8e14-4193-9e16-eda57f3affbe`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`e733c1b1-afd5-4940-9cf0-32ec2d3e4320`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.amazonMarketplaceVendor]: {
        connectionSteps: [
            {
                id: uniqueUuid`5bdde59b-592f-4da7-9bd1-11e1d090deb2`,
                type: DAG_DataSourceConnectionStepType.oauthAmazonMarketplace,
                scopes: "",
                isResumable: true,
            },
            {
                id: uniqueUuid`e3f050dd-8d78-4ca1-8489-138986bbd84e`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.amazonMarketplaceVendor}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`28dfdac5-0ac3-4438-b246-56aab1f2b146`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`5bdde59b-592f-4da7-9bd1-11e1d090deb2`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`ed228c70-98eb-4b03-ba52-67d103c876bc`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.facebookAds]: {
        connectionSteps: [
            {
                id: uniqueUuid`3d101f14-32b9-4a62-a66b-ca1aafbb638c`,
                type: DAG_DataSourceConnectionStepType.oauthFacebook,
                scopes: "ads_read, ads_management, email, public_profile, business_management",
                isResumable: true,
            },
            {
                id: uniqueUuid`ea432c69-69a0-40d9-9482-8b37ec8cc276`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.facebookAds}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`f3cd39b5-37a6-470a-b10e-56dcdccc626a`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    accessToken: {
                        stepId: uniqueUuid`3d101f14-32b9-4a62-a66b-ca1aafbb638c`,
                        stepDataAttributeName: "accessToken",
                    },
                },
            },
            {
                id: uniqueUuid`83ba0d76-4fc5-403a-a079-23c71cb2eb1e`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.facebookPageOverviewAndPosts]: {
        connectionSteps: [
            {
                id: uniqueUuid`ae5a788d-6698-4792-a20d-74f34c44c013`,
                type: DAG_DataSourceConnectionStepType.oauthFacebook,
                isResumable: true,
                scopes: "pages_show_list, pages_read_engagement, read_insights, pages_manage_metadata, pages_read_user_content",
            },
            {
                id: uniqueUuid`55df88fe-ae80-4b95-be8a-43dfe997e4ef`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.facebookPageOverviewAndPosts}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`3cace14c-82f9-4979-93e2-49948a20f9dc`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    userAccessToken: {
                        stepId: uniqueUuid`ae5a788d-6698-4792-a20d-74f34c44c013`,
                        stepDataAttributeName: "accessToken",
                    },
                },
            },
            {
                id: uniqueUuid`37fb4d12-5ae5-41e0-83f6-9933680c6414`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    // [DAG_DataSourceConnectionStrategy.facebookPageVideoAndReels]: {
    //     connectionSteps: [
    //         {
    //             id: uniqueUuid`3b0f99df-54ba-45e2-b336-187dddc5ea45`,
    //             type: DAG_DataSourceConnectionStepType.contactIntellsysTeam,
    //         },
    //     ],
    // },
    [DAG_DataSourceConnectionStrategy.flipkartAds]: {
        connectionSteps: [
            {
                id: uniqueUuid`a5bc61a3-9bd2-4fb1-9b02-cdcccbf97cc8`,
                type: DAG_DataSourceConnectionStepType.oauthFlipkart,
                isResumable: true,
                // TODO: Check scopes
                scopes: "reporting campaign_management",
            },
            {
                id: uniqueUuid`9a7f05d9-8b54-4e98-ae9d-8254c011792b`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.flipkartAds}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`a54794d4-53cb-4c8a-a59c-abde75cd9596`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`a5bc61a3-9bd2-4fb1-9b02-cdcccbf97cc8`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`7aca2bd7-0f23-482c-80ce-d9f9bac66316`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.googleAds]: {
        connectionSteps: [
            {
                id: uniqueUuid`a7b80f35-66f2-43f8-a69d-b7e4864a0835`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Google Ads API

The use and transfer to any other app of information received from Google APIs will adhere to [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

By selecting “I agree”, you consent to the terms outlined above. If you do not agree, you may not be able to access certain features of our application. Please review our [Privacy Policy](/privacy-policy) for more information regarding the use and protection of your data.`,
                continueButtonType: DAG_ContinueButtonType.googleAuth,
            },
            {
                id: uniqueUuid`2fa51636-e798-4c63-ba9e-5f8e48e033fb`,
                type: DAG_DataSourceConnectionStepType.oauthGoogle,
                isResumable: true,
                scopes: "https://www.googleapis.com/auth/adwords https://www.googleapis.com/auth/userinfo.email",
            },
            {
                id: uniqueUuid`8dee9249-3e86-4db3-9ae7-bb675b878314`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.googleAds}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`1ed3991d-ff31-455f-868a-8ffa1e96c72f`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`2fa51636-e798-4c63-ba9e-5f8e48e033fb`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`6f1c303d-6824-451c-8509-5493adfdece8`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.googleAnalytics]: {
        connectionSteps: [
            {
                id: uniqueUuid`01564c8e-8922-4eea-8d9c-c92156fc25f2`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Google Analytics API

The use and transfer to any other app of information received from Google APIs will adhere to [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

By selecting “I agree”, you consent to the terms outlined above. If you do not agree, you may not be able to access certain features of our application. Please review our [Privacy Policy](/privacy-policy) for more information regarding the use and protection of your data.`,
                continueButtonType: DAG_ContinueButtonType.googleAuth,
            },
            {
                id: uniqueUuid`22c71539-0b90-472f-b8d3-ea503618b031`,
                type: DAG_DataSourceConnectionStepType.oauthGoogle,
                isResumable: true,
                scopes: "https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/userinfo.email",
            },
            {
                id: uniqueUuid`074080a7-7c0b-4134-9aea-39999b15c17b`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.googleAnalytics}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`d5afbe8e-f5c8-428f-827e-dd445a8ad19d`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`22c71539-0b90-472f-b8d3-ea503618b031`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`d52ad750-2730-4865-9be7-367798cef6f1`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.googleMyBusiness]: {
        connectionSteps: [
            {
                id: uniqueUuid`dd08b3ff-893b-4d1e-afb3-417ee319124c`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Google My Business API

The use and transfer to any other app of information received from Google APIs will adhere to [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

By selecting “I agree”, you consent to the terms outlined above. If you do not agree, you may not be able to access certain features of our application. Please review our [Privacy Policy](/privacy-policy) for more information regarding the use and protection of your data.`,
                continueButtonType: DAG_ContinueButtonType.googleAuth,
            },
            {
                id: uniqueUuid`31fa8607-c27d-40a7-9c49-8bdd4a85323c`,
                type: DAG_DataSourceConnectionStepType.oauthGoogle,
                isResumable: true,
                scopes: "https://www.googleapis.com/auth/business.manage",
            },
            {
                id: uniqueUuid`5a1ee9e0-306e-4ebd-9881-eb027973f014`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.googleMyBusiness}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`4cadebb8-2881-4b79-9e55-0b21b3a705e8`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`31fa8607-c27d-40a7-9c49-8bdd4a85323c`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`e6b1b4a7-8d39-45fd-9526-2b1c5d1c661d`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.googleSearchConsole]: {
        connectionSteps: [
            {
                id: uniqueUuid`daa0e3ed-7124-4bf8-8768-3ce4b05cdb84`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Google Search Console API

The use and transfer to any other app of information received from Google APIs will adhere to [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

By selecting “I agree”, you consent to the terms outlined above. If you do not agree, you may not be able to access certain features of our application. Please review our [Privacy Policy](/privacy-policy) for more information regarding the use and protection of your data.`,
                continueButtonType: DAG_ContinueButtonType.googleAuth,
            },
            {
                id: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                type: DAG_DataSourceConnectionStepType.oauthGoogle,
                isResumable: true,
                // TODO: Confirm this
                scopes: "https://www.googleapis.com/auth/webmasters.readonly",
            },
            {
                id: uniqueUuid`8036cdfb-fc77-46ba-a166-5d3621d689b9`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.googleSearchConsole}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`307df8e9-e55b-48fe-9ed3-b2c9bec448d3`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`d6969781-3ded-4750-b233-655c2f67d747`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.instagram]: {
        connectionSteps: [
            {
                id: uniqueUuid`781287ab-010d-45a1-bcdd-61b1e2831631`,
                type: DAG_DataSourceConnectionStepType.oauthFacebook,
                isResumable: true,
                scopes: "instagram_basic, instagram_manage_insights, pages_show_list, pages_read_engagement",
            },
            {
                id: uniqueUuid`f172c056-985a-4ced-8491-b70397276760`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.instagram}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`95742651-2b57-4088-978d-006988aea43e`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    userAccessToken: {
                        stepId: uniqueUuid`781287ab-010d-45a1-bcdd-61b1e2831631`,
                        stepDataAttributeName: "accessToken",
                    },
                },
            },
            {
                id: uniqueUuid`2f6a93a0-dece-490f-8882-87636f8da241`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.linkedinAds]: {
        connectionSteps: [
            {
                id: uniqueUuid`daa0e3ed-7124-4bf8-8768-3ce4b05cdb84`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Linkedin Ads API`
,
                continueButtonType: DAG_ContinueButtonType.linkedinAuth,
            },
            {
                id: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                type: DAG_DataSourceConnectionStepType.oauthLinkedin,
                isResumable: true,
                scopes: "r_ads_reporting,r_basicprofile,rw_ads",
            },
            {
                id: uniqueUuid`8036cdfb-fc77-46ba-a166-5d3621d689b9`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.linkedinAds}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`307df8e9-e55b-48fe-9ed3-b2c9bec448d3`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`d6969781-3ded-4750-b233-655c2f67d747`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.linkedinPage]: {
        connectionSteps: [
            {
                id: uniqueUuid`daa0e3ed-7124-4bf8-8768-3ce4b05cdb84`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Linkedin Ads API`
,
                continueButtonType: DAG_ContinueButtonType.linkedinAuth,
            },
            {
                id: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                type: DAG_DataSourceConnectionStepType.oauthLinkedin,
                isResumable: true,
                scopes: "rw_organization_admin,r_basicprofile,r_organization_social,r_member_postAnalytics,r_member_profileAnalytics",
            },
            {
                id: uniqueUuid`8036cdfb-fc77-46ba-a166-5d3621d689b9`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.linkedinPage}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`307df8e9-e55b-48fe-9ed3-b2c9bec448d3`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`7a97f327-52e9-48a5-9dc0-28b992392905`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`d6969781-3ded-4750-b233-655c2f67d747`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.shopify]: {
        connectionSteps: [
            {
                id: uniqueUuid`789dabfc-7228-4795-b135-43817069aabb`,
                type: DAG_DataSourceConnectionStepType.contactIntellsysTeam,
                isResumable: true,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.unicommerce]: {
        connectionSteps: [
            {
                id: uniqueUuid`40f84652-00e2-4884-acfc-df7e2677dc76`,
                type: DAG_DataSourceConnectionStepType.contactIntellsysTeam,
                isResumable: true,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.userSupplied]: {
        connectionSteps: [
            {
                id: uniqueUuid`3f99add8-590a-4245-86b9-f5a034d37508`,
                type: DAG_DataSourceConnectionStepType.enterFieldsForUserSupplied,
                isResumable: true,
            },
            {
                id: uniqueUuid`53dbbb3d-9d5c-4dff-a2f0-9a4854a03cee`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {},
            },
            {
                id: uniqueUuid`1345c225-8e0f-44df-91da-d7a816173e56`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.youtube]: {
        connectionSteps: [
            {
                id: uniqueUuid`9f285fe1-f6bf-4801-9df3-444ee58496d8`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### Using Youtube API

The use and transfer to any other app of information received from Google APIs will adhere to [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

By selecting “I agree”, you consent to the terms outlined above. If you do not agree, you may not be able to access certain features of our application. Please review our [Privacy Policy](/privacy-policy) for more information regarding the use and protection of your data.`,
                continueButtonType: DAG_ContinueButtonType.googleAuth,
            },
            {
                id: uniqueUuid`e99d591e-132e-48b2-be1c-2c9667b5425a`,
                type: DAG_DataSourceConnectionStepType.oauthGoogle,
                isResumable: true,
                scopes: "https://www.googleapis.com/auth/youtube.readonly",
            },
            {
                id: uniqueUuid`7697a21c-f9ba-47f0-8903-30cf944e2813`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: `/connection-strategies/${DAG_DataSourceConnectionStrategy.youtube}/get-accessible-accounts`,
            },
            {
                id: uniqueUuid`d6921ffc-9ad8-43f0-aeb2-3d5f5eea5810`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {
                    refreshToken: {
                        stepId: uniqueUuid`e99d591e-132e-48b2-be1c-2c9667b5425a`,
                        stepDataAttributeName: "refreshToken",
                    },
                },
            },
            {
                id: uniqueUuid`76d85c53-378f-4c36-8914-7a043720e8e7`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.custom]: {
        connectionSteps: [
            {
                id: uniqueUuid`4511f265-d713-4b2c-9d8a-3b7a8748ea62`,
                type: DAG_DataSourceConnectionStepType.contactIntellsysTeam,
                isResumable: true,
            },
        ],
    },
    [DAG_DataSourceConnectionStrategy.uiTest]: {
        connectionSteps: [
            {
                id: uniqueUuid`0cd31107-308a-433b-bf2e-a1f83fa9fae4`,
                type: DAG_DataSourceConnectionStepType.consent,
                isResumable: true,
                consentText: `### UI Test Data Source\n\nThis is a test data source for UI testing purposes only.\n\nBy selecting "I agree", you confirm this is a test connection.`,
                continueButtonType: DAG_ContinueButtonType.default,
            },
            {
                id: uniqueUuid`a3f2e1d0-c9b8-4a76-8543-210fedcba987`,
                type: DAG_DataSourceConnectionStepType.selectAccount,
                isResumable: true,
                getAccountsQueryFn_pathname: UI_TEST_MOCK_ACCOUNTS_PATHNAME,
            },
            {
                id: uniqueUuid`65d9f417-e933-4ed2-b08f-f9109269ef77`,
                type: DAG_DataSourceConnectionStepType.connectDataSource,
                isResumable: false,
                connectionDataExtraFields: {},
            },
            {
                id: uniqueUuid`96e5497e-db8c-41a5-ba3b-510660942939`,
                type: DAG_DataSourceConnectionStepType.connectDataClusters,
                isResumable: false,
            },
        ],
    },
};

// // TODO: Add docs here
// export enum DAG_DataSourceDataPipelineId {
//     amazonAds = "9f07e443-d9b5-4dee-89bf-111363f25e8a",
//     easyEcom = "c06dcfd2-34ab-43cf-9e9d-127e54233420",
//     facebookAds = "50a65ef2-0c5f-4e7b-ab97-8dee4a2f3f66",
//     facebookPage = "45c01ab1-4fcc-405f-b94e-4e339242ba61",
//     googleAds = "c7e3930b-14a6-41e6-8df3-578dd54fc740",
//     googleAnalytics = "8f5d58d7-6b92-4b69-afea-893de1fb85d2",
//     instagram = "d86012bf-ad81-4108-8571-049eee90b582",
//     shopify = "ff8a4a8f-168c-4f47-89da-f2a96891fcf7",
//     tataTeleServices = "fbd045b3-711a-4fee-9d16-51fb465b0a96",
//     youtube = "df372997-2df2-433b-bb57-93aacf81f749",
//     zohoCrm = "22079807-3834-4eb1-8e23-96bc848248da",
// }

export type DAG_AccountAvailableForConnection = {
    accountId: string;
    accountName: string;
    disabled: boolean;
    connectionData: Record<string, unknown>;
};

export async function getAccountsQueryFn({
    accessToken,
    dataSourceId,
    extraFields,
    getAccountsQueryFn_pathname,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    dataSourceId: Uuid;
    extraFields: Record<string, unknown>;
    getAccountsQueryFn_pathname: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    //
}): Promise<Result<Array<DAG_AccountAvailableForConnection>>> {
    if (getAccountsQueryFn_pathname === UI_TEST_MOCK_ACCOUNTS_PATHNAME) {
        console.log("UI Test mock accounts pathname");
        return dataResult([
            {accountId: "ui-test-account-1", accountName: "UI Test Account 1", disabled: false, connectionData: {}},
            {accountId: "ui-test-account-2", accountName: "UI Test Account 2", disabled: false, connectionData: {}},
        ]);
    }
    const connectionStrategyId = getAccountsQueryFn_pathname.split("/")[2];

    const accountsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/xolotl${GET_ACCESSIBLE_ACCOUNTS_PATHNAME}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                connectionStrategyId: connectionStrategyId,
                payload: {
                    dataSourceTemplateId: dataSourceId,
                    ...extraFields,
                },
            }),
        },
        zod.array(
            zod
                .object({
                    accountId: zod.string(),
                    accountName: zod.string(),
                    isConnected: zod.boolean(),
                    connectionData: zod.record(zod.string(), zod.unknown()),
                })
                .transform((input) => ({
                    accountId: input.accountId,
                    accountName: input.accountName,
                    disabled: input.isConnected,
                    connectionData: input.connectionData,
                })),
        ),
    );
    if (accountsResult.success !== true) {
        return accountsResult;
    }
    const accounts = accountsResult.data;

    return dataResult(accounts);
}
